import axios from "axios";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import { Skeleton } from "@chakra-ui/react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import ActionsMenu from "../../components/ActionsMenu";
import BarData from "../../components/Charts/BarData";
import CircleChart from "../../components/Charts/CircleChart";
import ColumnCustomizer from "../../components/ColumnCustomizer";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialOfferFilterData } from "../../utils/initialData";
import LeadDataContainer from "../Leads/components/LeadDataContainer";
import AddOfferModal from "./AddOfferModal";
import DuplicateData from "./components/DuplicateData";
import ExcecutedLoiConvert from "./components/ExcecutedLoiConvert";
import OfferFilter from "./components/OfferFilter";
import EditOfferModal from "./EditOfferModal";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const Offers = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offersData, setOffersData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(3);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [offerDashboard, setOfferDashboard] = useState();
  const [leadId, setleadId] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRows, setExpandedRows] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false); // ← New State
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showExcecutedLoi, setShowExcecutedLoi] = useState(false);
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialOfferFilterData;
  });

  // ==================== COLUMN CUSTOMIZATION ====================
  const allColumns = [
    { key: 'details', label: 'Details', fixed: true },
    { key: 'offer_date', label: 'Offer Date', fixed: true },
    { key: 'property_name', label: 'Property Name' },
    { key: 'acquisition', label: 'Acquisition' },
    { key: 'disposition', label: 'Disposition' },
    { key: 'initial_counter', label: 'Initial or Counter' },
    { key: 'noi', label: 'NOI' },
    { key: 'offer_price', label: 'Offer Price' },
    { key: 'offer_cap_rate', label: 'Offer Cap Rate' },
    { key: 'asking_cap_rate', label: '  Asking Cap Rate' },
    { key: 'asking_price', label: ' Asking Price' },
    { key: 'offer_price_percentage', label: ' % of Asking Price' },
    { key: 'actions', label: 'Actions', fixed: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('offersVisibleColumns');
    return saved
      ? JSON.parse(saved)
      : allColumns.map(col => col.key); // Show all by default
  });
  // ============================================================

  const [filterStatus, setFilterStatus] = useState({
    offer_new_listing_quater: false,
    offer_total_inital: false,
    buyer_inital_offer: false,
    seller_inital_offer: false,
    buyerlead_buyerinital_offer_converstion_rate: false,
    sellerlead_sellerinital_offer_converstion_rate: false,
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState("");

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === "") {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  // ==================== Existing Logic ====================
  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;
    if (search) {
      params += `&search=${encodeURIComponent(search)}`;
    }

    const { associate, contact, property_name, property_address, created_at, created_end_date, created_start_date, city, state, zip_code, offer_from, deal_type, offer_type, noi, offer_cap_rate, offer_price, min_asking_cap_rate, max_asking_cap_rate, min_asking_price, max_asking_price, percent_of_asking_price,
      offer_new_listing_quater,
      offer_total_inital,
      buyer_inital_offer,
      seller_inital_offer,
      buyerlead_buyerinital_offer_converstion_rate,
      sellerlead_sellerinital_offer_converstion_rate, unique_record } = filters;

    if (associate) {
      params += `&broker=${encodeURIComponent(associate)}`;
    }
    if (contact?.id !== "") {
      params += `&contact=${encodeURIComponent(contact?.id)}`;
    }
    if (property_name) {
      params += `&property_name=${encodeURIComponent(property_name)}`;
    }
    if (property_address) {
      params += `&property_address=${encodeURIComponent(property_address)}`;
    }
    if (created_at) {
      params += `&created_at=${encodeURIComponent(created_at)}`;
    }
    if (created_start_date) {
      params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    }
    if (created_end_date) {
      params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    }
    if (city) {
      params += `&city=${encodeURIComponent(city)}`;
    }
    if (state) {
      params += `&state=${encodeURIComponent(state)}`;
    }
    if (zip_code) {
      params += `&zip_code=${encodeURIComponent(zip_code)}`;
    }
    if (offer_from) {
      params += `&offer_from=${encodeURIComponent(offer_from)}`;
    }
    if (deal_type) {
      params += `&deal_type=${encodeURIComponent(deal_type === "acquisition" ? 1 : 0)}`;
    }
    if (unique_record) {
      params += `&unique_record=${encodeURIComponent(unique_record)}`;
    }
    if (offer_type) {
      params += `&offer_type=${encodeURIComponent(offer_type)}`;
    }
    if (noi) {
      params += `&noi=${encodeURIComponent(noi)}`;
    }
    if (offer_cap_rate) {
      params += `&offer_cap_rate=${encodeURIComponent(offer_cap_rate)}`;
    }
    if (offer_price) {
      params += `&offer_price=${encodeURIComponent(offer_price)}`;
    }
    if (min_asking_cap_rate) {
      params += `&min_asking_cap_rate=${encodeURIComponent(min_asking_cap_rate)}`;
    }
    if (max_asking_cap_rate) {
      params += `&max_asking_cap_rate=${encodeURIComponent(max_asking_cap_rate)}`;
    }
    if (min_asking_price) {
      params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    }
    if (max_asking_price) {
      params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;
    }
    if (percent_of_asking_price) {
      params += `&percent_of_asking_price=${encodeURIComponent(percent_of_asking_price)}`;
    }
    if (offer_new_listing_quater) {
      params += `&offer_new_listing_quater=${encodeURIComponent(offer_new_listing_quater)}`;
    }
    if (offer_total_inital) {
      params += `&offer_total_inital=${encodeURIComponent(offer_total_inital)}`;
    }
    if (buyer_inital_offer) {
      params += `&buyer_inital_offer=${encodeURIComponent(buyer_inital_offer)}`;
    }
    if (seller_inital_offer) {
      params += `&seller_inital_offer=${encodeURIComponent(seller_inital_offer)}`;
    }
    if (buyerlead_buyerinital_offer_converstion_rate) {
      params += `&buyerlead_buyerinital_offer_converstion_rate=${encodeURIComponent(buyerlead_buyerinital_offer_converstion_rate)}`;
    }
    if (sellerlead_sellerinital_offer_converstion_rate) {
      params += `&sellerlead_sellerinital_offer_converstion_rate=${encodeURIComponent(sellerlead_sellerinital_offer_converstion_rate)}`;
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}`;
    }

    return params;
  };

  const fetchOffers = (filters) => {
    const queryParams = buildQueryParams({
      ...filters,
      offer_new_listing_quater: filterStatus.offer_new_listing_quater,
      offer_total_inital: filterStatus.offer_total_inital,
      buyer_inital_offer: filterStatus.buyer_inital_offer,
      seller_inital_offer: filterStatus.seller_inital_offer,
      buyerlead_buyerinital_offer_converstion_rate: filterStatus.buyerlead_buyerinital_offer_converstion_rate,
      sellerlead_sellerinital_offer_converstion_rate: filterStatus.sellerlead_sellerinital_offer_converstion_rate,
    });
    axios
      .get(`${BASE_URL}/offer-listing?status=${activeTab}&${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.Offerlist;
        console.log(">>>", value);

        setOffersData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOffers(filterData);
  }, [sortDirection, sortBy]);

  useEffect(() => {
    onSuccess();
  }, [search, currentPage, itemPerPage, activeTab]);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchOffers(filters);
    } else {
      fetchOffers(filterData);
    }
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-offer/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const fetchDashboardData = () => {
    setLoading(true);

    axios
      .get(`${BASE_URL}/dashboard-offer?active_tab=${activeTab}`, config)
      .then((res) => {
        setOfferDashboard(res?.data?.offer_dashboard);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboardData();
  }, [activeTab]);

  // ==================== Dropdown View Options ====================
  const viewOptions = [
    { value: 0, label: "My Dashboard" },
    { value: 1, label: "My Offers" },
    { value: 2, label: "Team Dashboard" },
    { value: 3, label: "Team Offers" },
  ];

  const handleViewChange = (e) => {
    const newTab = parseInt(e.target.value);
    setActiveTab(newTab);
    setCurrentPage(1);
    setExpandedRows({});
    // Reset filterStatus when switching tabs
    setFilterStatus({
      offer_new_listing_quater: false,
      offer_total_inital: false,
      buyer_inital_offer: false,
      seller_inital_offer: false,
      buyerlead_buyerinital_offer_converstion_rate: false,
      sellerlead_sellerinital_offer_converstion_rate: false,
    });
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id], // Toggle only the clicked row
    }));
  };

  const data = offerDashboard?.asking_price_graph;

  console.log(data, "data");

  const BarGraphData = [
    { name: "<$1M", count: offerDashboard?.count_greater_than_three_million },
    { name: "$1M-$3M", count: offerDashboard?.count_between_than_three_million },
    { name: ">$3M", count: offerDashboard?.count_less_than_million },
  ];

  const handleBoxClick = (statusKey) => {
    let targetTab;

    if (activeTab === 0) {
      targetTab = 1; // My Dashboard -> My Leads
    } else if (activeTab === 2) {
      targetTab = 3; // Team Dashboard -> Team Leads
    } else {
      targetTab = activeTab; // fallback (optional)
    }

    const newFilterStatus = {
      offer_new_listing_quater: false,
      offer_total_inital: false,
      buyer_inital_offer: false,
      seller_inital_offer: false,
      buyerlead_buyerinital_offer_converstion_rate: false,
      sellerlead_sellerinital_offer_converstion_rate: false,
      [statusKey]: true,
    };

    setActiveTab(targetTab);
    setCurrentPage(1);
    setFilterStatus(newFilterStatus);
  };

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Offers</p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            {/* Dropdown - Team Offers is Default */}
            <div className="relative min-w-[240px]">
              <select
                value={activeTab}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <OfferFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative flex-1 min-w-[200px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search city"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            {/* New Customize Columns Button */}
            {(activeTab === 1 || activeTab === 3) && (
              <button
                className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
                onClick={() => setShowColumnModal(true)}
              >
                Columns ⚙️
              </button>
            )}

            <button
              className="add-contact-button green-bg-H light-L body-S"
              onClick={() => {
                setSelectedId("");
                setShowAddModal(true);
              }}
            >
              <img className="mr-2" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      {(activeTab === 1 || activeTab === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {visibleColumns.includes("details") && <th>Details</th>}

                    {visibleColumns.includes("offer_date") && (
                      <th>
                        <div className="table-header">
                          OFFER DATE
                          <img
                            role="button"
                            src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("created_at");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("property_name") && (
                      <th>
                        <div className="table-header">
                          Property Name
                          <img
                            role="button"
                            src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("property_name");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("acquisition") && (
                      <th>
                        <div className="table-header">
                          Acquisition
                          <img
                            role="button"
                            src={sortBy !== "acquisition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("acquisition");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("disposition") && (
                      <th>
                        <div className="table-header">
                          Disposition
                          <img
                            role="button"
                            src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("disposition");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("initial_counter") && (
                      <th>
                        <div className="table-header">
                          Initial or Counter
                          <img
                            role="button"
                            src={sortBy !== "type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("type");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("noi") && (
                      <th>
                        <div className="table-header">
                          NOI
                          <img
                            role="button"
                            src={sortBy !== "annual_rent" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("annual_rent");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("offer_cap_rate") && (
                      <th>
                        <div className="table-header">
                          Offer Cap Rate
                          <img
                            role="button"
                            src={sortBy !== "offer_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("offer_cap_rate");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("offer_price") && (
                      <th>
                        <div className="table-header">
                          Offer Price
                          <img
                            role="button"
                            src={sortBy !== "offer_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("offer_price");
                              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("asking_cap_rate") && (
                      <th>
                        <div className="table-header">
                          Asking Cap Rate
                          <img
                            role="button"
                            src={sortBy !== "percent_of_asking_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("percent_of_asking_cap_rate");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("asking_price") && (
                      <th>
                        <div className="table-header">
                          Asking Price
                          <img
                            role="button"
                            src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("asking_price");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("offer_price_percentage") && (
                      <th>
                        <div className="table-header">
                          % of Asking Price
                          <img
                            role="button"
                            src={sortBy !== "percent_of_asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("percent_of_asking_price");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.includes("actions") && <th>Action</th>}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(10)].map((_, idx) => (
                      <tr key={idx} className="dark-H body-N">
                        {visibleColumns.includes("details") && <td><Skeleton height="20px" width="20px" /></td>}
                        {visibleColumns.includes("offer_date") && <td><Skeleton height="20px" width="80px" /></td>}
                        {visibleColumns.includes("property_name") && <td><Skeleton height="20px" width="140px" /></td>}
                        {visibleColumns.includes("acquisition") && <td><Skeleton height="20px" width="100px" /></td>}
                        {visibleColumns.includes("disposition") && <td><Skeleton height="20px" width="100px" /></td>}
                        {visibleColumns.includes("initial_counter") && <td><Skeleton height="20px" width="80px" /></td>}
                        {visibleColumns.includes("noi") && <td><Skeleton height="20px" width="80px" /></td>}
                        {visibleColumns.includes("offer_cap_rate") && <td><Skeleton height="20px" width="80px" /></td>}
                        {visibleColumns.includes("offer_price") && <td><Skeleton height="20px" width="100px" /></td>}
                        {visibleColumns.includes("asking_cap_rate") && <td><Skeleton height="20px" width="80px" /></td>}
                        {visibleColumns.includes("asking_price") && <td><Skeleton height="20px" width="100px" /></td>}
                        {visibleColumns.includes("offer_price_percentage") && <td><Skeleton height="20px" width="80px" /></td>}
                        {visibleColumns.includes("actions") && <td><Skeleton height="20px" width="40px" /></td>}
                      </tr>
                    ))
                  ) : offersData?.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="body-N text-center mt-6 p-10 border-0">
                        No Offers Available
                      </td>
                    </tr>
                  ) : (
                    offersData?.map((el, idx) => (
                      <React.Fragment key={idx}>
                        <tr className="dark-H body-N">
                        {visibleColumns.includes("details") && (
                          <td>
                            <img
                              role="button"
                              src={expandedRows[el.id] ? UpArrow : DownArrow}
                              alt="icon"
                              onClick={() => toggleRowDetails(el.id)}
                            />
                          </td>
                        )}

                        {visibleColumns.includes("offer_date") && (
                          <td>{moment(el?.created_at).tz("America/New_York").format("MM/DD/YY")}</td>
                        )}

                        {visibleColumns.includes("property_name") && (
                          <td className="head-5 capitalize">
                            <Link
                              to={
                                el?.portfolio === 0
                                  ? `/property/${el?.property_id}`
                                  : el?.link?.owner_info_type === "contact"
                                    ? `/contact/${el?.link?.owner?.id}`
                                    : `/company/${el?.link?.owner?.id}`
                              }
                              onClick={(e) => {
                                e.preventDefault();

                                if (el?.portfolio === 0) {
                                  navigate(`/property/${el?.property_id}`);
                                } else if (el?.link?.owner_info_type === "contact") {
                                  navigate(`/contact/${el?.link?.owner?.id}`);
                                } else if (el?.link?.owner_info_type === "company") {
                                  navigate(`/company/${el?.link?.owner?.id}`);
                                }
                              }}
                            >
                              {el?.link?.property_name} <br />
                              {el?.link?.street_address} <br />
                              {el?.link?.state && el?.link?.city ? `${el?.link?.city}, ${el?.link?.state}` : ""}
                            </Link>
                          </td>

                        )}

                        {visibleColumns.includes("acquisition") && (
                          <td>
                            <Link to={`/contact/${el?.buyer_contact_id}`}>
                              {el?.acquistion_name || ""}
                            </Link>
                          </td>
                        )}

                        {visibleColumns.includes("disposition") && (
                          <td>
                            <Link to={`/contact/${el?.seller_contact_id}`}>
                              {el?.disposition_name || ""}
                            </Link>
                          </td>
                        )}

                        {visibleColumns.includes("initial_counter") && (
                          <td>
                            <span className="tags">{el?.status_name}</span>
                          </td>
                        )}

                        {visibleColumns.includes("noi") && (
                          <td>{el?.link?.anual_rent ? `$${el.link.anual_rent.toLocaleString()}` : ""}</td>
                        )}

                        {visibleColumns.includes("offer_cap_rate") && (
                          <td>{el?.offer_cap_rate ? `${el.offer_cap_rate}%` : ""}</td>
                        )}

                        {visibleColumns.includes("offer_price") && (
                          <td>{el?.offer_price ? `$${el.offer_price.toLocaleString()}` : ""}</td>
                        )}

                        {visibleColumns.includes("asking_cap_rate") && (
                          <td className="text-center"> {el?.link?.asking_cap_rate ? `${el.link.asking_cap_rate.toLocaleString()}%` : ""}</td>
                        )}

                        {visibleColumns.includes("asking_price") && (
                          <td className="text-center"> {el?.link?.asking_price ? `$${el.link.asking_price.toLocaleString()}` : ""}</td>
                        )}

                        {visibleColumns.includes("offer_price_percentage") && (
                          <td className="text-center"> {el?.asking_price_format_percentage ?
                            `${el.asking_price_format_percentage.toLocaleString()}%` : ""}</td>
                        )}

                        {visibleColumns.includes("actions") && (
                          <td>
                            <div className="flex gap-3 justify-center">
                              <ActionsMenu
                                from="Offers"
                                handleEdit={() => {
                                  setShowEditModal(true);
                                  setSelectedId(el?.id);
                                  setleadId(el?.lead_id);
                                }}
                                handleDelete={() => {
                                  setSelectedId(el?.id);
                                  setShowDeleteModal(true);
                                }}
                                showOtherOption={true}
                                otherDuplicateTitle={"Duplicate Offer"}
                                handleDuplicateData={() => {
                                  setSelectedId(el?.id);
                                  setShowContractModal(true);
                                }}
                                otherConverttoExecutedLOI={<> Convert to <br />Executed LOI</>}
                                handleConverttoExecutedLOI={() => {
                                  setSelectedId(el?.id);
                                  setShowExcecutedLoi(true)
                                }}
                              />
                            </div>
                          </td>
                        )}
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="8">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="body-N dark-H">
                                      {el?.seller_broker?.length > 0 ? (
                                        el.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.seller_broker.length - 1 ? ", " : ""}
                                          </span>
                                        ))
                                      ) : (
                                        ""
                                      )}
                                    </div>


                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.seller_bds?.first_name} {el?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <p className="body-N dark-H">
                                      {el?.buyer_broker?.length > 0 ? (
                                        el.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index}>
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.buyer_broker.length - 1 ? ", " : ""}
                                          </span>
                                        ))
                                      ) : (
                                        ""
                                      )}
                                    </p>
                                  </div>



                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.buyer_bds?.first_name} {el?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  {/* <div>
                                    <p className="dark-H head-4">Acquistion</p>
                                    <Link
                                      to={`/contact/${el?.buyer_contact_id}`}
                                      className="body-N  dark-H "
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/contact/${el?.buyer_contact_id}`);
                                      }}
                                    >
                                      {el?.acquistion_name || ""}
                                    </Link>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4">Disposition</p>
                                    <Link
                                      to={`/contact/${el?.seller_contact_id}`}
                                      className="body-N  dark-H "
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/contact/${el?.seller_contact_id}`);
                                      }}
                                    >
                                      {el?.disposition_name || ""}
                                    </Link>
                                  </div> */}

                                  {/* <div>
                                    <p className="dark-H head-4 mb-2">Asking Cap Rate</p>
                                    <p className="body-N  dark-H ">
                                      {el?.property?.asking_cap_rate ? `${el.property.asking_cap_rate.toLocaleString()}%` : ""}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Price</p>
                                    <p className="body-N  dark-H ">
                                      {el?.property?.asking_price ? `$${el.property.asking_price.toLocaleString()}` : ""}
                                    </p>
                                  </div> */}

                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                  )}
                </tbody>
              </table>
          </div>

          {offersData?.length > 0 && (
            <ContactPagination
              activeTab={activeTab}
              from="offer"
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              itemPerPage={itemPerPage}
            />
          )}
        </div>
      )}

      {(activeTab === 0 || activeTab === 2) && (
        <>
          <div className="">
            <div className="w-auto">
              <div className="flex items-center gap-3">
                <LeadDataContainer
                  text="NEW INITIAL OFFERS THIS QUARTER"
                  number={offerDashboard?.new_initial_offer}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("offer_new_listing_quater")}
                />
                <LeadDataContainer
                  text="TOTAL INITIAL OFFERS YTD"
                  number={offerDashboard?.initial_offer_ytd}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("offer_total_inital")}
                />
                <LeadDataContainer
                  text="BUYER INITIAL OFFERS YTD"
                  number={offerDashboard?.buyer_initial_offer_ytd}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("buyer_inital_offer")}
                />
                <LeadDataContainer
                  text="SELLER INITIAL OFFERS YTD"
                  number={offerDashboard?.seller_initial_offer_ytd}
                  className="min-h-[8rem] w-full"
                  onClick={() => handleBoxClick("seller_inital_offer")}
                />
              </div>

              <div className="flex gap-3 ">
                <div className="w-[25%]">
                  <LeadDataContainer
                    text="BUYER LEAD TO BUYER INITIAL OFFER CONVERSION RATE"
                    number={offerDashboard?.buyer_lead_to_buyer_initial_offer_conversion_rate}
                    className="min-h-[8rem] w-full"
                    onClick={() => handleBoxClick("buyerlead_buyerinital_offer_converstion_rate")}
                  />
                  <LeadDataContainer
                    text="SELLER LEAD TO SELLER INITIAL OFFER CONVERSION RATE"
                    number={offerDashboard?.seller_lead_to_seller_initial_offer_conversion_rate}
                    className="min-h-[8rem] w-full "
                    onClick={() => handleBoxClick("sellerlead_sellerinital_offer_converstion_rate")}
                  />
                </div>

                <div className="flex gap-3 items-center w-[75%]">
                  <CircleChart
                    Title="% OF ASKING PRICE"
                    TotalTitle="Total Offers"
                    Total={offerDashboard?.total_offer_count}
                    data={data}
                  />

                  <BarData
                    className="w-[50%]"
                    Title="OFFER PRICE POINT"
                    BarGraphData={BarGraphData}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <AddOfferModal
        showModal={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCallApi={() => {
          setSelectedId("");
          onSuccess();
        }}
        id={selectedId}
      />

      <EditOfferModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("");
          onSuccess();
        }}
      />

      <DuplicateData
        showModal={showContractModal}
        onClose={() => setShowContractModal(false)}
        // handleUnderContract={handleUnderContract}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("");
          onSuccess();
        }}
      />

      <ExcecutedLoiConvert
        showModal={showExcecutedLoi}
        onClose={() => setShowExcecutedLoi(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("");
          onSuccess();
        }}
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />

      {/* Column Customizer Modal */}
      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="offersVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </BaseLayout>
  );
};

export default Offers;
